import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * Measures the rendered width of a host element so fixed-viewBox SVG charts
 * (PaceCurve, WaterfallBridge …) can be given an intrinsic width that matches
 * the column they sit in — instead of a 720/920px drawing scaled down to a
 * phone column, which shrinks every label to unreadable size.
 *
 * Returns `null` until the first measurement (callers fall back to their
 * default width), then live-updates via ResizeObserver. Safe in jsdom (no
 * ResizeObserver → one measurement on mount, if any).
 */
export function useContainerWidth<T extends HTMLElement = HTMLDivElement>(): [RefObject<T | null>, number | null] {
  const ref = useRef<T | null>(null)
  const [width, setWidth] = useState<number | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const next = Math.round(el.getBoundingClientRect().width)
      setWidth((prev) => (next > 0 && prev !== next ? next : prev))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure())
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return [ref, width]
}

/** Clamp a measured width into a chart's sensible drawing range. */
export function chartWidthFor(measured: number | null, fallback: number, min = 320, max = fallback): number {
  if (measured == null || !Number.isFinite(measured) || measured <= 0) return fallback
  return Math.max(min, Math.min(max, measured))
}
